import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { RuleForm } from '../components/ui/RuleForm';
import {
  ADD_ALLOCATION_CONDITION,
  CREATE_ALLOCATION_RULE,
} from '../../../api/queries/allocation';
import { stagingOptions } from '../../../constants/apollo';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';

const truncateToastText = (text: string | undefined | null, maxLength: number = 12): string => {
  if (!text) return text || '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

const CreateRule = () => {
  const navigate = useNavigate();
  const [createRule] = useMutation(CREATE_ALLOCATION_RULE, stagingOptions);
  const [addCondition] = useMutation(ADD_ALLOCATION_CONDITION, stagingOptions);
  const [saving, setSaving] = useState(false);

  const handleSave = async (data: any) => {
    try {
      setSaving(true);
      const createInput = {
        name: data.name,
        priority: data.priority === '' ? 0 : parseInt(data.priority, 10),
        resultVia: data.resultVia,
        resultData: data.resultData,
        description: data.description || undefined,
        isActive: data.isActive,
        ruleType: 'order_allocation',
      };

      const response = await createRule({
        variables: {
          input: createInput,
        },
      });

      const newRuleId = response?.data?.createAllocationRule?.allocationRule?.id;

      if (!newRuleId) {
        console.error('createAllocationRule payload missing id', response?.data?.createAllocationRule);
        throw new Error('Failed to retrieve the created rule ID.');
      }

      if (data.conditions?.length) {
        await Promise.all(
          data.conditions.map((condition: any) =>
            addCondition({
              variables: {
                ruleId: newRuleId,
                param: condition.parameter,
                operator: condition.operator,
                value: condition.value,
                cast: condition.castType || 'string',
              },
            })
          )
        );
      }

      toast.success(
        <div>
          <div className="font-semibold">Allocation rule created successfully!</div>
          <div className="text-sm mt-1">
            <strong>Name:</strong> {truncateToastText(data.name, 12)} | <strong>ID:</strong> {newRuleId}
          </div>
        </div>
      );
      navigate('/allocation', { state: { refresh: true } });
    } catch (error: any) {
      console.error('Failed to create allocation rule', error);
      const serverMessage =
        error?.graphQLErrors?.[0]?.message ||
        error?.message ||
        'Unable to create allocation rule.';
      toast.error(serverMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/allocation');
  };

  return (
    <div className="flex flex-col space-y-2 bg-white pb-12">
      <div className="main-wrapper">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => {
              if (window.history.length > 1) {
                navigate(-1);
              } else {
                navigate('/allocation');
              }
            }}
            className="flex items-center gap-2 text-gray-600 hover:text-primaryColor transition-colors mb-6 group"
          >
            <ArrowBackIcon
              sx={{ fontSize: '20px' }}
              className="group-hover:translate-x-[-2px] transition-transform"
            />
            <span className="text-sm font-medium">Back</span>
          </button>

          <div className="mb-8">
            <div className="bg-gradient-to-r from-primaryColor via-primaryColor to-darkBlue rounded-2xl p-8 shadow-xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24"></div>

              <div className="relative z-10">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-2xl border border-white/30">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 5V19M5 12H19" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h1 className="text-3xl font-bold text-white">Create New Strategy</h1>
                      <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-semibold text-white border border-white/30">
                        Draft
                      </span>
                    </div>
                    <p className="text-base text-white/90 leading-relaxed max-w-2xl">
                      Define a new automated routing logic. Configure conditions and result channels to optimize your fleet allocation.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl">
            <div className="max-w-4xl mx-auto">
              <RuleForm initialData={null} onSave={handleSave} onCancel={handleCancel} isSaving={saving} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateRule;

